/**
 * Internationalization (i18n) foundation.
 *
 * Supports English (default) and Italian.
 * Uses a lightweight custom implementation — no external i18n library
 * dependency needed for this scope.
 *
 * Future: can be migrated to react-i18next or lingui if needed.
 */

import { Locale, DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@dentai/shared';
import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

import { translations } from './translations.js';

export type { Locale };

export interface I18nContext {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, string>) => string;
  supportedLocales: Locale[];
}

const I18nContext = createContext<I18nContext | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window === 'undefined') return DEFAULT_LOCALE;
    const savedLocale = window.localStorage.getItem('dentai_locale');
    return savedLocale === Locale.IT || savedLocale === Locale.EN || savedLocale === Locale.AR
      ? savedLocale
      : DEFAULT_LOCALE;
  });

  const setLocale = useCallback((nextLocale: Locale) => {
    setLocaleState(nextLocale);
    if (typeof window !== 'undefined') window.localStorage.setItem('dentai_locale', nextLocale);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === Locale.AR ? 'rtl' : 'ltr';
    document.documentElement.style.textAlign = locale === Locale.AR ? 'right' : '';
  }, [locale]);

  const t = useCallback(
    (key: string, params?: Record<string, string>): string => {
      const resolve = (bundle: Record<string, unknown>): string | undefined => {
        let value: unknown = bundle;
        for (const segment of key.split('.')) {
          if (typeof value !== 'object' || value === null) return undefined;
          value = (value as Record<string, unknown>)[segment];
        }
        return typeof value === 'string' ? value : undefined;
      };

      const localized = resolve(translations[locale] ?? translations[DEFAULT_LOCALE]);
      const fallback = localized ?? resolve(translations[DEFAULT_LOCALE]);
      const value = fallback ?? key;

      return params
        ? value.replace(/\{(\w+)\}/g, (_match: string, parameter: string) => params[parameter] ?? '')
        : value;
    },
    [locale],
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, supportedLocales: SUPPORTED_LOCALES }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContext {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
